/**
 * Triggers a callback when the user clicks outside of the specified element
 * Accepts optional active prop to control when the listener is active
 *
 * @param ref - The ref to the element to listen for outside clicks
 * @param callback - The callback to trigger when the user clicks outside of the element
 * @param active - Whether the listener is active (optional)
 * @returns void
 */

import { useEffect, useCallback } from "react";

interface UseOutsideClickProps {
  ref: React.RefObject<HTMLElement | null> | undefined;
  callback: (event?: MouseEvent) => void;
  active?: boolean;
}

export const useOutsideClick = (props: UseOutsideClickProps) => {
  const { ref = undefined, callback, active = true } = props;

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (ref?.current && !ref.current.contains(event.target as Node) && active) {
        callback(event);
      }
    },
    [ref, active, callback],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [handleClick]);
};
